import { useCallback, useMemo, useState } from 'react'
import { Plus, Trash2, UserRound } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchTestUsers,
  createTestUser,
  deleteTestUser,
  DT_STR as S,
  type TestUserRow,
} from './devtoolsApi'

/**
 * A27.08 — Тестовые пользователи. Seed/delete throwaway accounts for QA. Dormant
 * until `/admin/devtools/test-users` ships. Placeholder permission USERS_VIEW.
 */
export function TestUsersScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [busy, setBusy] = useState(false)

  const seed = async () => {
    setBusy(true)
    try {
      await createTestUser()
      toast(S.seeded, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<TestUserRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'number' },
      { key: 'name', header: S.name, type: 'text' },
      { key: 'phone', header: S.phone, type: 'text' },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<TestUserRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.delete,
        icon: <Trash2 size={14} />,
        danger: true,
        onSelect: async (r) => {
          try {
            await deleteTestUser(r.id)
            toast(S.deleted, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchTestUsers, [])
  return (
    <>
      <PageHeader
        title={S.testUsers}
        screenId="A27.08"
        actions={
          <button
            type="button"
            onClick={seed}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Plus size={14} />
            {S.seed}
          </button>
        }
      />
      <DataTable<TestUserRow>
        key={reloadKey}
        screenId="A27.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <UserRound size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
